/*
 * Tooltip: a few words about a control or an icon. It opens on hover and on
 * keyboard focus, and on a tap on a touch screen, where there's no hover
 * (Radix's own tooltip ignores touch). A second tap, a tap elsewhere or
 * Escape closes it. A tap that opens it doesn't also follow a link around it.
 * A link or button as the trigger keeps its own tap (it navigates or acts).
 *
 * For an icon with no words of its own, pass `label`: the trigger then
 * carries the same words as its aria-label, so a screen reader hears them
 * without opening anything. Dark on the light theme and light on the dark
 * one, so it reads as a layer above the page in both.
 */
import { useRef, useState, type MouseEvent, type PointerEvent, type ReactNode } from "react";
import { Tooltip as T } from "radix-ui";

export const TooltipProvider = T.Provider;

export function Tooltip({
  content,
  children,
  side = "top",
  align = "center",
  label,
  disabled,
}: {
  content: ReactNode;
  /** One element that takes a ref (a button, a link, a focusable span). */
  children: ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
  /** The same words as the trigger's aria-label, for an icon that has none of its own. */
  label?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const touch = useRef(false);
  // Whether it was open when the tap began: Radix may close it on the press or open it on focus, and the tap decides.
  const wasOpen = useRef(false);
  const trigger = useRef<HTMLButtonElement>(null);
  if (disabled) return <>{children}</>;
  return (
    <T.Root open={open} onOpenChange={setOpen}>
      <T.Trigger
        asChild
        ref={trigger}
        aria-label={label}
        data-tip=""
        onPointerDown={(e: PointerEvent) => {
          touch.current = e.pointerType === "touch" || e.pointerType === "pen";
          wasOpen.current = open;
        }}
        onClick={(e: MouseEvent) => {
          // A link or a button keeps its own tap: it does what it says, as it would without a tooltip.
          if (!touch.current || (e.currentTarget as HTMLElement).matches("a, button, input, select, textarea, [role=button]")) return;
          // A tap toggles the words. Stopping here keeps Radix from closing them again, and a link or row around the icon from opening.
          e.preventDefault();
          e.stopPropagation();
          setOpen(!wasOpen.current);
        }}
      >
        {children}
      </T.Trigger>
      <T.Portal>
        <T.Content
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={8}
          // A tap on the trigger itself is the toggle above, not a tap "outside".
          onPointerDownOutside={(e) => {
            if (trigger.current?.contains(e.target as Node)) e.preventDefault();
          }}
          className="hx-pop z-[60] max-w-xs rounded-md bg-carbon px-2.5 py-1.5 text-[12.5px] leading-snug text-white shadow-[0_8px_20px_-6px_rgba(15,23,42,.4)] dark:bg-cream-100 dark:text-carbon"
        >
          {content}
        </T.Content>
      </T.Portal>
    </T.Root>
  );
}
